import type { RuntimeLogin, RuntimeLoginStatus, RuntimeProfile } from '@deskworks/shared-types';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CheckCircle2, ExternalLink, Loader2, LogIn, ShieldCheck, XCircle } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CopyButton } from '@/components/code-block';
import { RelativeTime } from '@/components/relative-time';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { formatAbsolute, formatSince } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { t, tr } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { qk } from '@/lib/query-keys';

const ACTIVE: readonly RuntimeLoginStatus[] = ['starting', 'waiting_for_user', 'completing'];
const FAILED: readonly RuntimeLoginStatus[] = ['failed', 'expired', 'cancelled'];

export function providerName(runtimeType: RuntimeProfile['runtimeType']): string {
  return runtimeType === 'codex_subscription' ? 'ChatGPT' : 'Claude';
}

/** Refreshes everything that shows a runtime profile's credential state. */
export function invalidateRuntimeProfiles(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (k[0] === 'ws' && (k[2] === 'runtimeProfiles' || k[2] === 'agents')) || k[0] === 'agent';
    },
  });
}

/**
 * "Connect Claude/ChatGPT": starts a login container for a subscription profile,
 * polls its state every 2 s and walks the user through the provider's sign-in.
 */
export function RuntimeLoginDialog({
  profile,
  open,
  onOpenChange,
  onSucceeded,
}: {
  profile: Pick<RuntimeProfile, 'id' | 'name' | 'runtimeType'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once when the login succeeded (e.g. to assign the profile to an agent). */
  onSucceeded?: () => void;
}) {
  const queryClient = useQueryClient();
  const onSucceededRef = useRef(onSucceeded);
  onSucceededRef.current = onSucceeded;
  const succeededFor = useRef<string | null>(null);
  const [loginId, setLoginId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const provider = providerName(profile.runtimeType);

  const start = useMutation({
    mutationFn: () => api.runtimeProfiles.startLogin(profile.id),
    onMutate: () => {
      setStartError(null);
      setLoginId(null);
    },
    onSuccess: (login) => {
      queryClient.setQueryData(qk.runtimeLogin(login.id), login);
      setLoginId(login.id);
    },
    onError: (err) => setStartError(errorMessage(err)),
  });

  // Start a fresh login whenever the dialog opens (once per opening, also under StrictMode).
  const startedRef = useRef(false);
  const startRef = useRef(start.mutate);
  startRef.current = start.mutate;
  useEffect(() => {
    if (open && !startedRef.current) {
      startedRef.current = true;
      startRef.current();
    }
    if (!open) {
      startedRef.current = false;
      setLoginId(null);
      setStartError(null);
    }
  }, [open]);

  const login = useQuery({
    queryKey: qk.runtimeLogin(loginId ?? ''),
    queryFn: () => api.runtimeLogins.get(loginId!),
    enabled: open && !!loginId,
    refetchInterval: (q) => (q.state.data && ACTIVE.includes(q.state.data.status) ? 2000 : false),
    refetchIntervalInBackground: true,
  });
  const data = login.data;
  const status: RuntimeLoginStatus | null = data?.status ?? null;
  const active = start.isPending || (status !== null && ACTIVE.includes(status));

  useEffect(() => {
    if (status !== 'succeeded' || !loginId || succeededFor.current === loginId) return;
    succeededFor.current = loginId;
    invalidateRuntimeProfiles(queryClient);
    onSucceededRef.current?.();
  }, [status, loginId, queryClient]);

  const cancel = useMutation({
    mutationFn: () => (loginId ? api.runtimeLogins.cancel(loginId) : Promise.resolve(null)),
  });

  const requestClose = (next: boolean) => {
    if (next) return onOpenChange(true);
    if (active) setConfirmClose(true);
    else onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent className="sm:max-w-lg" onInteractOutside={(e) => active && e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>{t('Connect {provider}', { provider })}</DialogTitle>
            <DialogDescription>
              {t('The login runs in its own container. Deskworks only stores the result, encrypted – Deskworks never sees your password.')}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-32">
            {startError ? (
              <Failed message={startError} />
            ) : !data || start.isPending ? (
              <Spinner text={t('Starting login container …')} hint={t('This usually takes 5–20 seconds.')} />
            ) : (
              <LoginBody login={data} provider={provider} />
            )}
            {login.isError && data && ACTIVE.includes(data.status) && (
              <p className="mt-3 text-xs text-muted-foreground">{t('Connection to Deskworks briefly interrupted — still trying …')}</p>
            )}
          </div>

          <DialogFooter>
            {status === 'succeeded' ? (
              <Button onClick={() => onOpenChange(false)}>{t('Done')}</Button>
            ) : (status && FAILED.includes(status)) || startError ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  {t('Close')}
                </Button>
                <Button onClick={() => start.mutate()} loading={start.isPending}>
                  {t('Try again')}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => requestClose(false)}>
                {t('Cancel')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('Cancel login?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('Signing in with {provider} isn’t finished yet. If you close now, the login container will be stopped.', { provider })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('Continue signing in')}</AlertDialogCancel>
            <Button
              variant="destructive"
              loading={cancel.isPending}
              onClick={async () => {
                try {
                  await cancel.mutateAsync();
                } catch {
                  /* closing anyway; the login expires on its own */
                }
                setConfirmClose(false);
                onOpenChange(false);
              }}
            >
              {t('Cancel login')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function LoginBody({ login, provider }: { login: RuntimeLogin; provider: string }) {
  switch (login.status) {
    case 'starting':
      return <Spinner text={t('Starting login container …')} hint={t('This usually takes 5–20 seconds.')} />;
    case 'completing': {
      // Claude: after the code was submitted the backend goes straight to `completing` ("Checking code …").
      const text = login.provider === 'claude' && login.codeSubmitted ? t('Checking code …') : t('Finishing …');
      const hint = login.message && login.message.replace(/\s*…$/, '') !== text.replace(/\s*…$/, '') ? login.message : undefined;
      return <Spinner text={text} hint={hint} />;
    }
    case 'succeeded':
      return (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
          <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div>
            <div className="font-medium">{t('Connected')}</div>
            <div className="mt-0.5 text-sm opacity-90">
              {login.message ?? t('Your {provider} subscription is connected. Agents with this profile can now get to work.', { provider })}
            </div>
          </div>
        </div>
      );
    case 'failed':
    case 'expired':
    case 'cancelled':
      return (
        <Failed
          message={
            login.message ??
            (login.status === 'expired'
              ? t('Signing in took too long and expired.')
              : login.status === 'cancelled'
                ? t('Signing in was cancelled.')
                : t('Signing in failed.'))
          }
        />
      );
    case 'waiting_for_user':
      return login.provider === 'codex' ? <CodexSteps login={login} /> : <ClaudeSteps login={login} />;
  }
}

function CodexSteps({ login }: { login: RuntimeLogin }) {
  return (
    <ol className="flex flex-col gap-4">
      <Step n={1} title={t('Open the link')}>
        <OpenLink url={login.verificationUrl} />
      </Step>
      <Step n={2} title={t('Enter this code at OpenAI')}>
        {login.userCode ? (
          <div className="flex items-center gap-2">
            <code className="rounded-lg border bg-muted/50 px-4 py-2 font-mono text-2xl font-semibold tracking-[0.2em]">{login.userCode}</code>
            <CopyButton value={login.userCode} label={t('Copy code')} className="size-9" />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t('The code will appear in a moment …')}</p>
        )}
      </Step>
      <Step n={3} title={t('Deskworks is waiting for your confirmation …')} active>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> {t('As soon as you’ve confirmed at OpenAI, this continues automatically.')}
        </p>
        <Expiry login={login} />
      </Step>
    </ol>
  );
}

function ClaudeSteps({ login }: { login: RuntimeLogin }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const submit = useMutation({
    mutationFn: () => api.runtimeLogins.submitCode(login.id, code.trim()),
    onSuccess: (res) => queryClient.setQueryData(qk.runtimeLogin(login.id), res),
  });
  const checking = login.codeSubmitted || submit.isPending;

  return (
    <ol className="flex flex-col gap-4">
      <Step n={1} title={t('Open the link and sign in with your Claude account')}>
        <OpenLink url={login.verificationUrl} />
      </Step>
      <Step n={2} title={t('Paste the code from your browser')} active>
        {checking ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" /> {t('Checking code …')}
          </p>
        ) : (
          <form
            className="flex min-w-0 flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.trim()) submit.mutate();
            }}
          >
            {/* Codes and tokens are long single words: wrap them inside the dialog instead of widening it. */}
            <Textarea
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\s+/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (code.trim()) submit.mutate();
                }
              }}
              rows={3}
              placeholder={t('Paste the code here (or a token from claude setup-token)')}
              className="w-full min-w-0 resize-none font-mono text-xs break-all"
              autoComplete="off"
              spellCheck={false}
              aria-label={t('Code from your browser')}
            />
            <Button type="submit" className="self-end" disabled={!code.trim()} loading={submit.isPending}>
              {t('Connect')}
            </Button>
          </form>
        )}
        {submit.isError && <p className="mt-1.5 text-xs text-destructive">{errorMessage(submit.error)}</p>}
        {login.message && !checking && <p className="mt-1.5 text-xs text-muted-foreground">{login.message}</p>}
        <Expiry login={login} />
      </Step>
    </ol>
  );
}

function Step({ n, title, active, children }: { n: number; title: string; active?: boolean; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
          active ? 'border-primary bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
        )}
      >
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 text-sm font-medium">{title}</div>
        {children}
      </div>
    </li>
  );
}

function OpenLink({ url }: { url: string | null }) {
  if (!url) return <p className="text-sm text-muted-foreground">{t('The link will appear in a moment …')}</p>;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Button asChild variant="outline" className="w-fit">
        <a href={url} target="_blank" rel="noreferrer noopener">
          <ExternalLink /> {t('Open link')}
        </a>
      </Button>
      <div className="flex min-w-0 items-center gap-1">
        <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground" title={url}>
          {url}
        </span>
        <CopyButton value={url} label={t('Copy link')} />
      </div>
    </div>
  );
}

function Expiry({ login }: { login: RuntimeLogin }) {
  return (
    <p className="mt-2 text-[11px] text-muted-foreground">
      {tr('Expires {time}', { time: <RelativeTime value={login.expiresAt} /> })}
    </p>
  );
}

function Spinner({ text, hint }: { text: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <Loader2 className="size-6 animate-spin text-primary" />
      <div className="text-sm font-medium">{text}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function Failed({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
      <XCircle className="mt-0.5 size-5 shrink-0 text-red-600 dark:text-red-400" />
      <div className="text-sm break-words">{message}</div>
    </div>
  );
}

/** Small "connected since" / "not connected" line for subscription profiles. */
export function SubscriptionStatus({ profile }: { profile: Pick<RuntimeProfile, 'hasCredential' | 'credentialUpdatedAt'> }) {
  useNow();
  const since = formatSince(profile.credentialUpdatedAt);
  return profile.hasCredential ? (
    <span
      className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
      title={profile.credentialUpdatedAt ? t('Connected on {date}', { date: formatAbsolute(profile.credentialUpdatedAt) }) : undefined}
    >
      <ShieldCheck className="size-3" />
      {profile.credentialUpdatedAt && !since ? t('Just connected') : since ? t('Connected {since}', { since }) : t('Connected')}
    </span>
  ) : (
    <span className="text-amber-700 dark:text-amber-400">{t('Not connected')}</span>
  );
}

/** "Connect Claude/ChatGPT" button with its own login dialog. */
export function ConnectSubscriptionButton({
  profile,
  size = 'sm',
}: {
  profile: Pick<RuntimeProfile, 'id' | 'name' | 'runtimeType' | 'hasCredential'>;
  size?: 'sm' | 'xs';
}) {
  const [open, setOpen] = useState(false);
  const provider = providerName(profile.runtimeType);
  return (
    <>
      <Button variant={profile.hasCredential ? 'ghost' : 'default'} size={size} onClick={() => setOpen(true)}>
        <LogIn /> {profile.hasCredential ? t('Reconnect') : t('Connect {provider}', { provider })}
      </Button>
      <RuntimeLoginDialog profile={profile} open={open} onOpenChange={setOpen} />
    </>
  );
}
